import { yupResolver } from "@hookform/resolvers/yup";
import { FieldValidationMessages, FieldValidationsError } from "@wexinc/wex-common-web";
import {
  AddOrEditModal,
  CheckboxField,
  FieldValidation,
  Label,
  LoadableContentContainer,
  ScrollableContainer,
  TextAreaField,
  TextField,
  ToggleField,
  UseFormReturn,
  getErrorMessage,
} from "@wexinc/wex-ui";
import React, { useEffect, useRef, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import useCOPOrganizationService, {
  AddOrganization,
  BaseOrganizationInfo,
  Organization,
  OrganizationInfo,
  OrganizationLoginConfig,
  ParentOrganizationInfo,
} from "services/organization";
import { useCopSecurityService } from "services/security";
import * as yup from "yup";

interface Props {
  onAddedOrUpdated: (isNew: boolean, organization: OrganizationInfo) => void;
  onCancel: () => void;
  existingOrganization?: OrganizationInfo;
  parentOrganization?: ParentOrganizationInfo;
}

interface AddOrUpdateOrganizationSchema {
  organization: {
    name: string;
    description: string | null;
  };
  accessType: boolean;
  contactEmail: string | null;
  usernameAndPasswordEnabled: boolean;
  idpElections: { [id: string]: boolean };
  parentOrganizationName?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = yup.object<AddOrUpdateOrganizationSchema>().shape<{ [key: string]: any }>(
  {
    organization: yup.object().shape({
      name: yup.string().trim().required("Organization Name is required.").max(50, "Name cannot exceed 50 characters"),
      description: yup.string().nullable().notRequired().trim().max(255, "Description cannot exceed 255 characters"),
    }),
    contactEmail: yup
      .string()
      .nullable()
      .notRequired()
      .trim()
      .max(100, "Contact email cannot exceed 100 characters")
      .when("contactEmail", {
        is: (emailString: string) => emailString?.length,
        then: (rule) => rule.email("Must provide a valid email"),
      }),
    accessType: yup.boolean(),
    usernameAndPasswordEnabled: yup.boolean(),
    idpElections: yup.object(),
    idpsMustBeSpecified: yup.string().when(["usernameAndPasswordEnabled", "idpElections"], {
      is: (usernameAndPasswordEnabled: boolean, idpElections: { [id: string]: boolean }) => {
        return !usernameAndPasswordEnabled && Object.values(idpElections).some((i) => i === true) === false;
      },
      then: () => yup.string().required("Must specifiy at least one Login Method."),
    }),
    // this looks weird but the cyclic dependency is needed when validating a non-required field: https://stackoverflow.com/a/68939406 - MW 12/1/22
  },
  [["contactEmail", "contactEmail"]],
);

interface FormValues {
  organization: BaseOrganizationInfo;
  accessType: boolean;
  contactEmail: string | null;
  usernameAndPasswordEnabled: boolean;
  idpElections: { [id: string]: boolean };
  parentOrganizationName?: string;
}

const AddOrUpdateOrganizationModal: React.FC<Props> = (props) => {
  const copSecurityService = useCopSecurityService();
  const copOrganizationService = useCOPOrganizationService();
  const [customValidationMessages, setCustomValidationMessages] = useState<FieldValidationMessages>();
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>("Loading...");
  const { existingOrganization, onAddedOrUpdated, ...rest } = props;
  const useFormReturn = useRef<UseFormReturn>();
  const allIdps = copOrganizationService.getAllIdps();

  // We set default values for checkboxes because all checkboxes need values initially so they
  // aren't temporarily treated by react as uncontrolled comopnents.
  const defaultValues = {
    idpElections: allIdps.reduce((prevValue, idp) => {
      prevValue[idp.id] = false;
      return prevValue;
    }, {} as { [key: string]: boolean }),
    accessType: false,
    usernameAndPasswordEnabled: false,
  };

  let parentOrganization = rest.parentOrganization;

  if (parentOrganization && existingOrganization && parentOrganization.id === existingOrganization.id) {
    parentOrganization = undefined;
  }

  function onFormInit(useFormReturnIncoming: UseFormReturn) {
    useFormReturn.current = useFormReturnIncoming;
  }

  async function onSubmit(formValues: FormValues) {
    setLoadingMessage("Saving...");
    setCustomValidationMessages(undefined);

    const identityProviders = Object.entries(formValues.idpElections as never)
      .filter((i) => i[1] === true)
      .map((i) => i[0]) as string[];

    const organization: Organization | AddOrganization = {
      name: formValues.organization.name,
      description: formValues.organization.description,
      accessType: formValues.accessType ? "INTERNAL" : "EXTERNAL",
      contactEmail: formValues.contactEmail || null,
      parentOrganizationId: parentOrganization?.id,
      loginConfig: {
        usernameAndPasswordEnabled: formValues.usernameAndPasswordEnabled,
        identityProviderIds: identityProviders,
      },
    };

    if (existingOrganization) {
      (organization as Organization).id = existingOrganization.id;
    }

    try {
      const addedOrUpdatedOrganization = await copOrganizationService.addOrUpdateOrganization(
        !existingOrganization,
        organization,
      );
      if (!existingOrganization) {
        await copSecurityService.updatePermissions();
      }
      onAddedOrUpdated(!existingOrganization, addedOrUpdatedOrganization);
    } catch (e) {
      setLoadingMessage(undefined);
      if (e instanceof FieldValidationsError) {
        setCustomValidationMessages(e.fieldValidationMessages);
      } else {
        throw e;
      }
    }
  }

  useEffect(() => {
    async function onLoad() {
      function getDefaultFormValues(
        organization: BaseOrganizationInfo,
        loginConfig: OrganizationLoginConfig,
      ): FormValues {
        return {
          organization,
          accessType: organization.accessType === "INTERNAL",
          contactEmail: organization.contactEmail,
          usernameAndPasswordEnabled: loginConfig.usernameAndPasswordEnabled,
          idpElections: allIdps!.reduce((prevValue, idp) => {
            prevValue[idp.id] = loginConfig.identityProviderIds
              ? loginConfig.identityProviderIds!.indexOf(idp.id) !== -1
              : false;
            return prevValue;
          }, {} as { [key: string]: boolean }),
          parentOrganizationName: parentOrganization?.name,
        };
      }

      if (existingOrganization) {
        const loginConfig = await copOrganizationService.getOrganizationLoginConfiguration(existingOrganization.id);

        useFormReturn.current?.reset(getDefaultFormValues(existingOrganization, loginConfig));
      } else {
        useFormReturn.current?.reset(
          getDefaultFormValues(copOrganizationService.generateNewOrganization(), {
            usernameAndPasswordEnabled: false,
            identityProviderIds: [],
          }),
        );
      }

      setLoadingMessage(undefined);
    }

    onLoad();
  }, []);

  function onRealtimeChanges(fieldValues: FieldValues) {
    const formValues = fieldValues as FormValues;

    if (formValues.usernameAndPasswordEnabled || Object.values(formValues.idpElections).some((i) => i === true)) {
      useFormReturn.current?.clearErrors("idpsMustBeSpecified");
    }
  }

  return (
    <AddOrEditModal
      formProps={{
        onSubmit,
        options: {
          defaultValues,
          resolver: yupResolver(schema),
        },
        onInit: onFormInit,
        onRealtimeChanges: onRealtimeChanges,
      }}
      {...rest}
      titleOrHeader={existingOrganization ? `Edit ${existingOrganization.name}` : "New Organization"}
    >
      <LoadableContentContainer
        showLoading={!!loadingMessage}
        message={loadingMessage}
      />

      <>
        {parentOrganization && (
          <TextField
            readonly
            name="parentOrganizationName"
            label="Parent organization"
            className="!w-96"
          />
        )}
        <TextField
          name="organization.name"
          label="Name"
          className="!w-96"
          showRequiredDot
          externallySuppliedValidationMessage={customValidationMessages?.["name"]}
        />
        <TextAreaField
          containerClassName="!w-96"
          name="organization.description"
          label="Description"
        />
        <ToggleField
          name="accessType"
          label="Internal Organization"
          disabled={!copSecurityService.userHasGlobalPermission("editOrganization")}
          containerClassName="mb-6"
        />
        <TextField
          name="contactEmail"
          label="Contact Email"
          className="!w-96"
        />
        <ScrollableContainer className="max-h-52 border-neutral-dark-30 border rounded-lg px-4 mb-4">
          <IdpElectionsFieldValidation fieldName="idpsMustBeSpecified" />
          <Label className="pt-2 pb-3 block">Allowed Login Methods</Label>
          <ul>
            <li>
              <CheckboxField
                name="usernameAndPasswordEnabled"
                label="Username and Password"
              />
            </li>
            {allIdps?.map((i) => (
              <li key={i.id}>
                <CheckboxField
                  name={`idpElections[${i.id}]`}
                  label={i.name}
                />
              </li>
            ))}
          </ul>
        </ScrollableContainer>
      </>
    </AddOrEditModal>
  );
};

const IdpElectionsFieldValidation: React.FC<{ fieldName: string }> = (props) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <FieldValidation
      className="py-2"
      testId={`${props.fieldName}-field-validation`}
      error={getErrorMessage(errors, props.fieldName)?.toString()}
    />
  );
};

export default AddOrUpdateOrganizationModal;